import { useApp } from '../../context/AppContext';
import {
    ChevronRight,
    ClipboardList,
    Crown,
    AlertTriangle,
    AlertCircle,
    Circle,
    User
} from 'lucide-react';
import { useState } from 'react';
import type { Ticket, User as UserType } from '../../types';

export function DepartmentQueueView() {
    const { tickets, departments, users, currentUser, updateTicket, setActiveView } = useApp();
    const [assigningTicketId, setAssigningTicketId] = useState<string | null>(null);

    // Get departments where current user is a head
    const myHeadDepts = departments.filter(d => d.head_ids?.includes(currentUser?.id || ''));

    if (myHeadDepts.length === 0) {
        return (
            <div className="chat-area" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16 }}>
                <Crown size={48} strokeWidth={1} opacity={0.3} />
                <h3 style={{ color: 'var(--text-secondary)' }}>Department Queue</h3>
                <p style={{ color: 'var(--text-muted)' }}>You are not a head of any department.</p>
            </div>
        );
    }

    // Get tickets assigned to departments where I am head (via assigned_to_department_id)
    const queuedTickets = tickets.filter(t =>
        t.assigned_to_department_id && myHeadDepts.some(d => d.id === t.assigned_to_department_id)
    );

    // Get users in my headed departments
    const getDeptUsers = (deptId: string): UserType[] => {
        return users.filter(u =>
            u.department_id === deptId ||
            (u.department_ids && u.department_ids.includes(deptId))
        );
    };

    const getPriorityIcon = (priority: string) => {
        const props = { size: 14, strokeWidth: 2 };
        switch (priority) {
            case 'urgent': return <AlertTriangle {...props} color="var(--accent-danger)" />;
            case 'high': return <AlertCircle {...props} color="var(--color-warning)" />;
            case 'medium': return <Circle {...props} color="var(--accent-info)" />;
            case 'low': return <Circle {...props} color="var(--text-muted)" opacity={0.5} />;
            default: return null;
        }
    };

    const handleAssign = async (ticket: Ticket, userId: string) => {
        await updateTicket(ticket.id, {
            assignee_ids: [userId],
            assigned_to_department_id: null // Clear dept queue assignment
        });
        setAssigningTicketId(null);
    };

    return (
        <div className="chat-area" style={{ display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <header className="chat-header" style={{ padding: 'var(--spacing-md)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <ClipboardList size={24} strokeWidth={1.5} color="var(--accent-primary)" />
                    <div>
                        <h1 style={{ fontSize: 18, fontWeight: 600 }}>Department Queue</h1>
                        <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                            Tickets assigned to your department{myHeadDepts.length > 1 ? 's' : ''}
                        </p>
                    </div>
                </div>
            </header>

            {/* Queue List */}
            <div style={{ flex: 1, overflow: 'auto', padding: 'var(--spacing-md)' }}>
                {myHeadDepts.map(dept => {
                    const deptQueue = queuedTickets.filter(t => t.assigned_to_department_id === dept.id);
                    const deptUsers = getDeptUsers(dept.id);

                    return (
                        <div key={dept.id} style={{ marginBottom: 24 }}>
                            {/* Department Header */}
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                marginBottom: 12,
                                paddingBottom: 8,
                                borderBottom: '1px solid var(--border-color)'
                            }}>
                                <div style={{ width: 12, height: 12, borderRadius: '50%', background: dept.color }} />
                                <span style={{ fontWeight: 600, fontSize: 14 }}>{dept.name}</span>
                                <span style={{
                                    background: 'var(--bg-tertiary)',
                                    padding: '2px 8px',
                                    borderRadius: 10,
                                    fontSize: 11,
                                    color: 'var(--text-muted)'
                                }}>
                                    {deptQueue.length} ticket{deptQueue.length !== 1 ? 's' : ''} in queue
                                </span>
                            </div>

                            {deptQueue.length === 0 ? (
                                <p style={{ color: 'var(--text-muted)', fontSize: 13, fontStyle: 'italic', padding: '12px 0' }}>
                                    No tickets in queue.
                                </p>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                    {deptQueue.map(ticket => (
                                        <div
                                            key={ticket.id}
                                            style={{
                                                background: 'var(--bg-secondary)',
                                                borderRadius: 8,
                                                padding: 12,
                                                border: '1px solid var(--border-color)'
                                            }}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                                                {/* Ticket Info */}
                                                <div
                                                    style={{ flex: 1, cursor: 'pointer' }}
                                                    onClick={() => setActiveView({ type: 'ticket', id: ticket.id })}
                                                >
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                                        <span style={{
                                                            fontWeight: 600,
                                                            color: 'var(--accent-primary)',
                                                            fontSize: 12
                                                        }}>
                                                            #{ticket.ticket_number}
                                                        </span>
                                                        <span className={`priority-badge ${ticket.priority}`} style={{ fontSize: 10, padding: '2px 6px' }}>
                                                            {getPriorityIcon(ticket.priority)}
                                                            {ticket.priority}
                                                        </span>
                                                    </div>
                                                    <h4 style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>{ticket.title}</h4>
                                                    {ticket.description && (
                                                        <p style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.4 }}>
                                                            {ticket.description.substring(0, 100)}{ticket.description.length > 100 ? '...' : ''}
                                                        </p>
                                                    )}
                                                </div>

                                                {/* Assign Button / Dropdown */}
                                                <div style={{ position: 'relative' }}>
                                                    <button
                                                        className="btn btn-primary"
                                                        style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, padding: '6px 12px' }}
                                                        onClick={() => setAssigningTicketId(assigningTicketId === ticket.id ? null : ticket.id)}
                                                    >
                                                        <User size={14} />
                                                        Assign
                                                        <ChevronRight size={14} style={{
                                                            transform: assigningTicketId === ticket.id ? 'rotate(90deg)' : 'none',
                                                            transition: 'transform 0.15s'
                                                        }} />
                                                    </button>

                                                    {/* Assignment Dropdown */}
                                                    {assigningTicketId === ticket.id && (
                                                        <div style={{
                                                            position: 'absolute',
                                                            top: '100%',
                                                            right: 0,
                                                            marginTop: 4,
                                                            background: 'var(--bg-primary)',
                                                            border: '1px solid var(--border-color)',
                                                            borderRadius: 8,
                                                            padding: 8,
                                                            minWidth: 200,
                                                            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                                                            zIndex: 100
                                                        }}>
                                                            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8, textTransform: 'uppercase' }}>
                                                                Assign to team member
                                                            </div>
                                                            {deptUsers.map(user => (
                                                                <div
                                                                    key={user.id}
                                                                    onClick={() => handleAssign(ticket, user.id)}
                                                                    style={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        gap: 8,
                                                                        padding: '8px 10px',
                                                                        borderRadius: 6,
                                                                        cursor: 'pointer',
                                                                        background: 'transparent',
                                                                        transition: 'background 0.15s'
                                                                    }}
                                                                    onMouseOver={(e) => e.currentTarget.style.background = 'var(--bg-secondary)'}
                                                                    onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                                                                >
                                                                    <div style={{
                                                                        width: 28,
                                                                        height: 28,
                                                                        borderRadius: '50%',
                                                                        background: `hsl(${user.name.charCodeAt(0) * 10 % 360}, 60%, 45%)`,
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        color: 'white',
                                                                        fontWeight: 600,
                                                                        fontSize: 11
                                                                    }}>
                                                                        {user.name.charAt(0).toUpperCase()}
                                                                    </div>
                                                                    <div style={{ flex: 1 }}>
                                                                        <div style={{ fontSize: 13, fontWeight: 500 }}>{user.name}</div>
                                                                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{user.email}</div>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
